import { createSelector } from 'reselect'
import payloadTypes from 'helpers/enums/interactiveVideo/payloadTypes'
import sessionTypes from 'helpers/enums/sessionTypes'
import {
	defaultCurrentActivity,
	findActivityByUid,
	findBreakpointActivityByUid,
	getBreakpointsActivities,
} from 'redux/reducers/interactiveVideoReducer.js'
import userRoles from 'helpers/enums/userRoles'

const getInteractiveVideoSession = state => state.interactiveVideo

const NoSubmissionObject = null

const defaultPayload = {
	submissions: [],
}

const defaultResponse = {
	currentActivity: defaultCurrentActivity,
	submission: NoSubmissionObject,
}

export default createSelector(
	[ getInteractiveVideoSession ],
	interactiveVideo => getCurrentActivity(interactiveVideo)
)

function getCurrentActivity(interactiveVideo) {
	const sessionsBySessionType = {
		[sessionTypes.HOMEWORK]: getCurrentActivityForStudentPaced,
		[sessionTypes.LIVE_SESSION]: getCurrentActivityForLive,
		[sessionTypes.FRONT_OF_CLASSROOM]:
			getCurrentActivityForFrontOfClassroom,
		[sessionTypes.PREVIEW]:
			getCurrentActivityForFrontOfClassroom,
		[sessionTypes.UNSET]: getDefaultResponse,
	}
	return sessionsBySessionType[interactiveVideo.sessionType](interactiveVideo)
}

function getDefaultResponse() {
	return defaultResponse
}

function getCurrentActivityForLive(interactiveVideo) {
	const sessionsByRole = {
		[userRoles.STUDENT]: getCurrentActivityForLiveStudent,
		[userRoles.TEACHER]: getCurrentActivityForLiveTeacher,
	}
	return sessionsByRole[interactiveVideo.role](interactiveVideo)
}

function getCurrentActivityForStudentPaced(interactiveVideo) {
	const sessionsByRole = {
		[userRoles.STUDENT]: getCurrentActivityForStudentPacedStudent,
		[userRoles.TEACHER]: getCurrentActivityForStudentPacedTeacher,
	}
	return sessionsByRole[interactiveVideo.role](interactiveVideo)
}

function getCurrentActivityForFrontOfClassroom(interactiveVideo) {
	return getCurrentActivityForLive(interactiveVideo)
}

function getCurrentActivityForStudent(currentActivity, drafts) {
	const type = currentActivity.type
	const payload = currentActivity[payloadTypes[type]] || defaultPayload
	const draft = drafts[payload.uid]
	return {
		currentActivity: {
			payload,
			type,
		},
		draft,
	}
}

function getCurrentActivityForStudentPacedStudent(interactiveVideo) {
	const participantUid = interactiveVideo.participantUid
	const currentActivity = getCurrentActivityByParticipant(
		interactiveVideo.participants,
		participantUid,
		interactiveVideo.activities,
		interactiveVideo.media.breakpoints,
		interactiveVideo.participantState,
	)
	return getCurrentActivityForStudent(currentActivity, interactiveVideo.drafts)
}

function getCurrentActivityForLiveStudent(interactiveVideo) {
	return getCurrentActivityForStudent(interactiveVideo.currentActivity, interactiveVideo.drafts)
}

function getCurrentActivityForStudentPacedTeacher(interactiveVideo) {
	return getCurrentActivityForLiveTeacher(interactiveVideo)
}

function getCurrentActivityForLiveTeacher(interactiveVideo) {
	const currentActivity = interactiveVideo.currentActivity
	const type = currentActivity.type
	const payload = currentActivity[payloadTypes[type]] || defaultPayload
	const submission = null
	return {
		currentActivity: {
			payload,
			type,
		},
		submission,
	}
}

function getCurrentActivityByParticipant(
	participants,
	participantUid,
	activities,
	breakpoints,
	participantState,
) {
	if ( participantUid === '' ) {
		return defaultCurrentActivity
	}
	const participant =
		findCurrentParticipantByUid(
			participants,
			participantUid,
		)
	let currentActivity
	if ( participant ) {
		const currentBreakpointActivityUid =
			participantState.currentBreakpointActivityUid
		if (currentBreakpointActivityUid) {
			const breakpointsActivities = getBreakpointsActivities(breakpoints)

			const breakpointActivity =
				findBreakpointActivityByUid(
					breakpointsActivities,
					currentBreakpointActivityUid
				)

			currentActivity =
				findActivityByUid(activities, breakpointActivity.activityUid)
		}
	}
	currentActivity = currentActivity || defaultCurrentActivity
	return currentActivity
}

function findCurrentParticipantByUid(
	participants,
	participantUid,
) {
	return participants.find(
		participant => participant.uid === participantUid
	)
}
